import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import authService from "./authService";

const initialState = {
  token: null,
  id: null,
  role_id: null,
  isLoading: false,
  message: "",
  isSuccess: false,
  stores: [],
  tempCodeOnSignUp: null,
  tempCode: null,
};

export const login = createAsyncThunk("auth/login", async (user, thunkAPI) => {
  try {
    return await authService.login(user);
  } catch (err) {
    const message = err.response || err.response.data;
    return thunkAPI.rejectWithValue(message);
  }
});

export const getStores = createAsyncThunk(
  "auth/stores",
  async (user, thunkAPI) => {
    try {
      return await authService.getStores(user);
    } catch (err) {
      const message = err.response || err.response.data;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getStoresWithMobile = createAsyncThunk(
  "auth/stores-mobile",
  async (userData, thunkAPI) => {
    try {
      return await authService.getStoresWithMobile(userData);
    } catch (err) {
      const message = err.response || err.response.data;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const verifyOtp = createAsyncThunk(
  "auth/verify-otp",
  async (user, thunkAPI) => {
    try {
      return await authService.verifyOtp(user);
    } catch (err) {
      const message = err.response || err.response.data;
      return thunkAPI.rejectWithValue(message);
    }
  }
);
export const verifyOtpWithMobile = createAsyncThunk(
  "auth/verify-otp-with-mobile",
  async (user, thunkAPI) => {
    try {
      return await authService.verifyOtpWithMobile(user);
    } catch (err) {
      const message = err.response || err.response.data;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const signUp = createAsyncThunk(
  "auth/sign_up",
  async (user, thunkAPI) => {
    try {
      return await authService.signUp(user);
    } catch (err) {
      const message = err.response || err.response.data;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const logout = createAsyncThunk("auth/logout", async (_, thunkAPI) => {
  try {
    return await authService.logout();
  } catch (err) {
    const message = err.response || err.response.data;
    return thunkAPI.rejectWithValue(message);
  }
});

// New auth flow actions
export const sendEmailOtp = createAsyncThunk(
  "auth/sendEmailOtp",
  async (userData, thunkAPI) => {
    try {
      const response = await authService.sendEmailOtp(userData);
      console.log("sendEmailOtp", sendEmailOtp)
      return response.data;
    } catch (err) {
      const message = err.response?.data || err.message;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const verifyEmailOtp = createAsyncThunk(
  "auth/verifyEmailOtp",
  async (userData, thunkAPI) => {
    try {
      const response = await authService.verifyEmailOtp(userData);
      return response.data;
    } catch (err) {
      const message = err.response?.data || err.message;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getEmailToken = createAsyncThunk(
  "auth/getEmailToken",
  async (userData, thunkAPI) => {
    try {
      const response = await authService.getEmailToken(userData);
      return response.data;
    } catch (err) {
      const message = err.response?.data || err.message;
      return thunkAPI.rejectWithValue(message);
    }
  }
);
export const getMobileToken = createAsyncThunk(
  "auth/getMobileToken",
  async (userData, thunkAPI) => {
    try {
      const response = await authService.getMobileToken(userData);
      return response.data;
    } catch (err) {
      const message = err.response?.data || err.message;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    reset: (state) => {
      state.token = null;
      state.id = null;
      state.role_id = null;
      state.isLoading = false;
      state.message = "";
      state.isSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signUp.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(signUp.rejected, (state, action) => {
        state.token = null;
        state.isLoading = false;
        state.message = action.payload || "Sign Up failed";
        state.isSuccess = false;
      })
      .addCase(getStores.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getStores.fulfilled, (state, action) => {
        state.isSuccess = true;
        state.isLoading = false;

        state.stores = action.payload.data.stores;
      })
      .addCase(getStores.rejected, (state, action) => {
        state.isLoading = false;
        state.message = action.payload || "Login failed";
      })
      .addCase(getStoresWithMobile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getStoresWithMobile.fulfilled, (state, action) => {
        state.isSuccess = true;
        state.isLoading = false;
        state.stores = action.payload.data.stores;
      })
      .addCase(getStoresWithMobile.rejected, (state, action) => {
        state.isLoading = false;
        state.message = action.payload || "Mobile login failed";
      })
      .addCase(login.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isSuccess = true;
        state.isLoading = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.message = action.payload || "Login failed";
      })
      .addCase(verifyOtp.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.isSuccess = true;
        state.isLoading = false;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.message = action.payload || "Login failed";
      })
      .addCase(verifyOtpWithMobile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(verifyOtpWithMobile.fulfilled, (state, action) => {
        // Updated to properly handle the fulfilled state
        state.token = action.payload.token;
        state.id = action.payload.data.id;
        state.role_id = action.payload.data.role_id;
        state.isSuccess = true;
        state.isLoading = false;
        state.stores = action.payload.data.stores;
        state.tempCode = action.payload.data.code;
      })
      .addCase(verifyOtpWithMobile.rejected, (state, action) => {
        state.isLoading = false;
        state.message = action.payload || "Login failed";
      })
      
      .addCase(logout.fulfilled, (state) => {
        state.token = null;
        state.id = null;
        state.role_id = null;
        state.isLoading = false;
        state.message = "";
      })
      .addCase(logout.rejected, (state, action) => {
        state.message = action.payload || "Logout failed";
      })
      // New email flow cases
      .addCase(sendEmailOtp.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(sendEmailOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.tempCodeOnSignUp = action.payload.message.code;
      })
      .addCase(sendEmailOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        state.message = action.payload;
      })
      .addCase(verifyEmailOtp.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(verifyEmailOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.stores = action.payload.data.stores;
        state.tempCode = action.payload.data.code;
      })
      .addCase(verifyEmailOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        state.message = action.payload;
      })
      .addCase(getEmailToken.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getEmailToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.token = action.payload.token;
        state.id = action.payload.data.id;
        state.role_id = action.payload.data.role?.id;
      })
      .addCase(getEmailToken.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        state.message = action.payload;
      })
      .addCase(getMobileToken.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getMobileToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.token = action.payload.token;
        state.id = action.payload.data.id;
        state.role_id = action.payload.data.role?.id;
      })
      .addCase(getMobileToken.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        state.message = action.payload;
      })
  },
});

export const { reset } = authSlice.actions;

export default authSlice.reducer;
export const selectCurrentUser = (state) => state.auth;
